import type { FileResponse } from 'src/services/utils/utils.types';
import type { UpdateSetting } from 'src/services/settings/settings.types';
import { RejectionReason } from 'src/services/applications/applications.types';

import { TenantType } from 'src/types/enums';

import type { PageOptions } from '../api.types';
import { AccountRole } from './organisation.enum';
import type { AccountStatus } from '../account/account.types';

export enum PaymentCollectionType {
  AUTOMATIC_COLLECTION = 'automatic_collection',
  INVOICED = 'invoiced'
}

export interface JobDescriptionGenerationResponse {
  description: string;
}

export interface ProfileSummaryGenerationResponse {
  summary: string;
}

export enum ApplicationApplySourceStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export enum ExportFieldType {
  ASSIGNED_ACCOUNT_NAME = 'assigned_account_name',
  JOB_TITLE = 'job_title',

  CANDIDATE_NAME = 'candidate_name',
  CANDIDATE_EMAIL = 'candidate_email',

  LINKEDIN_PROFILE = 'linkedin_profile',
  FACEBOOK_PROFILE = 'facebook_profile',
  X_PROFILE = 'x_profile',
  GITHUB_PROFILE = 'github_profile',

  FREE_TEXT = 'free_text',
}

export type ExporterFieldMappingDto = {
  field_type: ExportFieldType;
  label: string;
}

export enum CompanySizeRange {
  // recruiter specific
  ONE_TWO_STAFF = '1_2',
  THREE_FIVE_STAFF = '3_5',
  SIX_TEN_STAFF = '6_10',
  ELEVEN_TWENTY_STAFF = '11_20',
  TWENTY_ONE_AND_UP = '21_',

  // client specific
  LESS_THAN_50 = '_50',
  FIFTY_ONE_TO_100 = '51_100',
  ONE_HUNDRED_AND_ONE_TO_TWO_HUNDRED = '101_200',
  TWO_HUNDRED_AND_ONE_TO_FIVE_HUNDRED = '201_500',
  FIVE_HUNDRED_AND_ONE_AND_UP = '501_',
}

export interface SetupOrganisationDTO {
  company_name: string;
  company_information: string;
  country_code: string;
  company_size: CompanySizeRange;
  tax_id: string;
}

export interface ReferenceQuestionDto {
  question: string;
  required: boolean;
  order: number;
}

export interface UpdateRejectionTemplateDto {
  templates: {
    reason: string;
    message: string;
  }[]
}

export interface IRejectionTemplate {
  reason: RejectionReason,
  message: string
}

export interface GetDepartmentQuery {
  department_id: string;
  org_id: string;
}

export type RemoveAccountsProps = GetDepartmentQuery & {
  accounts: AccountIDObj[];
};

export interface AccountIDObj {
  account_id: string;
}

export type AccountAndRoleObj = AccountIDObj & {
  department_role: string;
};

export type AddDepartmetAccountProps = GetDepartmentQuery & {
  accounts: AccountAndRoleObj[];
};

export interface EditCompanyProfileRequestProps {
  company_profile: {
    company_name: string;
    company_information?: string | null;
    company_logo_id?: string | null;
    tax_id?: string | null;
    company_size?: CompanySizeRange | null;
    country_code?: string | null;
  };
  org_id: string;
}

export interface GetDepartmentsQuery {
  page: PageOptions;
  org_id: string;
  search_query?: string;
}

export interface CreateDepartmentRequestProps {
  department: Omit<Department, 'id'>;
  org_id: string;
}

export interface EditDepartmentRequestProps {
  department: Omit<Department, 'id'>;
  org_id: string;
  department_id: string;
}

export interface DeleteDepartmentRequestProps {
  org_id: string;
  department_id: string;
}

export interface Department {
  id: string;
  name: string;
  description?: string;
  location?: string;
}

export interface GetDomainsQuery {
  page: PageOptions;
  org_id: string;
  search_query?: string;
}

export interface User {
  department_role?: AccountRole;
  id?: string;
  org_role?: AccountRole;
  email?: string;
  email_verified?: boolean;
  first_name: string;
  last_name: string;
  phone?: string;
  country_code?: string;
  phone_verified?: boolean;
  status?: AccountStatus;
  profile_image?: {
    id: string;
    public_path: string;
  };
  type: TenantType;
  user_id?: string;
  engage_access?: boolean;
}

export interface UpdateEngageAccessProps {
  org_id: string;
  account_id: string;
  engage_access: boolean;
}

export interface GetUsersProps {
  page: PageOptions;
  org_id: string;
  search_query?: string;
}

export interface GetPendingInvitesProps {
  page: PageOptions;
  search_query?: string;
  org_id: string;
}

export interface InviteUserDTO {
  org_id: string;
  invites: UserInvite[];
}

export interface RemoveInviteDTO {
  org_id: string;
  invite_id: string;
}

export interface UpdateUserRoleProps {
  org_id: string;
  org_role: string;
  account_id: string;
}

export interface UserInvite {
  email: string;
  role: string;
  departments?: string[];
  default_department_role?: string;
}

export interface UpdateApplySourcesDTO {
  org_id: string;
  sources: {
    source: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
  }[];
}

export interface InviteUsersResponse {
  email: string;
  result: 'success' | 'error';
  error: string | null;
}

export interface Invite {
  id: string;
  email: string;
  status: InviteStatus;
  tenant_type: TenantType;
  role: string;
  invite_url: string;
  created_at: string;
}

export enum InviteStatus {
  Pending = 'pending',
  Expired = 'expired',
}

export interface CompanyDomain {
  id: string;
  domain: string;
  verified: boolean;
}

export type UpdateSettingProps = {
  settings: UpdateSetting[];
  organizationId: string;
};

export type UpdateBrandSettingProps = {
  primary_color?: string;
  secondary_color?: string;
  brand_logo_id?: string | null;
  exporter_header_image_id?: string | null;
  exporter_footer_image_id?: string | null;
  exporter_cover_field_mapping?: any;
  exporter_font?: string | null;
  organizationId: string;
};

export interface IOrgBrandSettings {
  primary_color: string;
  secondary_color: string;
  brand_logo: FileResponse;
  exporter_header_image: FileResponse;
  exporter_footer_image: FileResponse;
  exporter_cover_field_mapping: any;
  exporter_font: string;
}

export type ApplicationApplySource = {
  id: string;
  source: string;
  status: ApplicationApplySourceStatus;
  /** Optional UTM values stamped onto a shared job link when this source is picked. */
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  created_at: Date;
  updated_at: Date;
}

export enum NoteTypeScopeType {
  SYSTEM = 'system',
  TENANT = 'tenant',
}

export enum NoteTypeStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

/**
 * A note type. `system_key` is set only on the four types JobTetris ships, which every organisation
 * shares and nobody can rename or archive - its presence is what tells the UI a row is read only.
 */
export type NoteType = {
  id: string;
  name: string;
  icon: string;
  colour: string;
  scope_type: NoteTypeScopeType;
  system_key: string | null;
  status: NoteTypeStatus;
  created_at: Date;
  updated_at: Date;
}

export type NoteTypeInput = {
  name: string;
  icon: string;
  colour: string;
}

export type CreateNoteTypeDTO = NoteTypeInput & { org_id: string };

export type UpdateNoteTypeDTO = Partial<NoteTypeInput> & { org_id: string; note_type_id: string };

export type NoteTypeStatusDTO = { org_id: string; note_type_id: string };

/**
 * The two reason lists each organisation edits in Settings (JT1-1800).
 */
export enum OrganizationReasonType {
  ApplicationWithdrawal = 'application_withdrawal',
  JobClose = 'job_close',
}

export type OrganizationReason = {
  id: string;
  type: OrganizationReasonType;
  label: string;
  position: number;
}

export type UpdateOrganizationReasonsDTO = {
  org_id: string;
  type: OrganizationReasonType;
  // the whole list in display order; reasons left out are archived
  reasons: { id?: string; label: string }[];
}
export interface WeekDaySetting {
  day: number;
  start: number;
  end: number;
}

export interface CustomDateSetting {
  date: Date;
  name: string;
}

export interface DateSettingsResponse {
  weekday_settings: WeekDaySetting[];
  custom_date_settings: CustomDateSetting[];
}

export type UpdateOrgDateSettingProps = {
  weekday_settings: WeekDaySetting[];
  custom_date_settings: CustomDateSetting[];
  organizationId: string;
};

export type ActivateSubTenantProps = {
  accounts: { id: string, role: string; }[];
};

export type MigrateAccountDto = {
  account_id: string;
  role: string;
  departments?: string[];
  default_department_role?: string;
};

export enum OrganizationStatus {
  INACTIVE = 'inactive',
  /** Agency/employer tenant awaiting activation by a JobTetris team member. */
  PENDING_ACTIVATION = 'pending_activation',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  ARCHIVED = 'archived',
}


export enum OrganizationSetting {
  STOP_GAP_HOURS = 'stop_gap_hours',
  REFERENCE_TEMPLATE_QUESTIONS_EDITABLE = 'reference_template_questions_editable',
  REJECTION_TEMPLATE_MESSAGES_EDITABLE = 'rejection_template_messages_editable',

  SILENT_REJECTION_ENABLED = 'silent_rejection_enabled',

  MAX_INTERVIEW_PHASES = 'max_interview_phases',

  AUTO_INVITE_APPLICANTS = 'auto_invite_applicants',
  APPLICATION_RECEIVED_TEMPLATE_ID = 'application_received_template_id',
  LOCAL_REJECTION_TEMPLATE_ID = 'local_rejection_template_id',
  JOB_BOARD_CAREERS_URL = 'job_board_careers_url',
  JOB_BOARD_LINK_MODE = 'job_board_link_mode',
  TEAM_REPORTS_ENABLED = 'team_reports_enabled',
}

/** Where an organization's shared job links point. Mirrors the backend JobBoardLinkMode. */
export enum JobBoardLinkMode {
  HOSTED = 'hosted',
  WEBSITE_QUERY = 'website_query',
  WEBSITE_PATH = 'website_path',
}